import { ChevronLeftCircleIcon, Info } from "lucide-react";
import { ReactNode, useState } from "react";

import HighlightBrick from "@/components/HighlightBrick";
import ColumnTabs, {
  type ColumnTab,
  type ColumnTabId,
  columnTabId,
} from "./ColumnTabs";
import HighlightsPlaylistColumn from "./HighlightsPlaylistColumn";
import InvestigationDetails, {
  hasInvestigationSections,
  SessionResearch,
  sessionResearch,
} from "./InvestigationDetails";
import { MdDragHandle } from "react-icons/md";
import PlaybackSettings from "../PlaybackSettings";
import { ProjectInfo } from "../../../../../APITypes/ProjectInfo.cs";
import type { PublicInvestigation } from "@/lib/public-investigation";
import type { PublicSessionNotes } from "@/lib/public-notes";
import type { PublicSegmentKinds } from "@/lib/public-segments";
import SessionNotesList from "./SessionNotesList";
import { usePlayAt } from "./usePlayAt";
import { cn } from "../../../../../lib/utils";
import { motion } from "framer-motion";
import { segment } from "../../../../../APITypes/Segment.cs";
import { useDataContext } from "../../../../../app/_context";

type ListedSegment = {
  segment: segment;
  kind: "talk" | "realtime" | "external";
  /** Index in the session's highlights; null for anything else. */
  highlightIndex: number | null;
};

// Talk highlights, research the recorder showed in real time, and the parts of
// an uploaded video its author named, in the order they play. Sped-up research
// is never listed (it's the bulk of the recording, not something anyone
// named), and neither is an unnamed piece of an upload (the desktop splits
// long uploads into pieces). With no kinds stored (projects recorded before
// real-time work existed) this is the highlights alone.
function listedSegments(
  item: { highlights?: segment[]; segments?: segment[] } | undefined,
  kinds: PublicSegmentKinds,
): ListedSegment[] {
  const highlights: ListedSegment[] = (item?.highlights ?? []).map((s, index) => ({
    segment: s,
    kind: "talk",
    highlightIndex: index,
  }));
  const others: ListedSegment[] = [];
  for (const s of item?.segments ?? []) {
    if (s.isHighlight) continue;
    const kind = kinds[String(s.id).toLowerCase()];
    if (kind === "realtime" || (kind === "external" && s.title?.trim())) {
      others.push({ segment: s, kind, highlightIndex: null });
    }
  }
  if (!others.length) return highlights;
  return [...highlights, ...others].sort((a, b) => a.segment.start - b.segment.start);
}

const SECTION_HEADING = "mb-2 px-1 text-sm font-semibold";

export default function HighlightsPlaylist(props: {
  pid: string;
  meta: ProjectInfo;
  widgetMode: boolean;
  /** Public session notes, keyed by session id. */
  notes?: PublicSessionNotes;
  investigation?: PublicInvestigation | null;
  /** What each segment is; {} for older projects. */
  segmentKinds?: PublicSegmentKinds;
}): ReactNode {
  // CONTEXT /////
  const {
    currentPlaylist,
    currentSessionIndex,
    currentPlaybackTime,
    isBusinessCardPlaying,
    isHighlightsOnlyMode,
  } = useDataContext();
  const [isFindingsShowing, setIsFindingsShowing] = useState(false);
  const [selectedTab, setSelectedTab] = useState<ColumnTabId>("highlights");
  const playAt = usePlayAt();

  // The session's own material sits in one list: what plays (highlights,
  // shown work, named parts of an upload), then the notes its author made
  // public, then the research linked to it. The whole investigation, when the
  // project has one, is the only other tab; without it there are no tabs.
  const currentItem = currentPlaylist[currentSessionIndex];
  const listed = listedSegments(currentItem, props.segmentKinds ?? {});
  const sessionNotes =
    (currentItem?.id && props.notes?.[currentItem.id.toLowerCase()]) || [];
  const investigation = hasInvestigationSections(props.investigation)
    ? props.investigation
    : null;
  const research = sessionResearch(investigation, currentItem?.id);
  // A session without a highlights stream plays in full even in
  // highlights-only mode (see PublicPageComponent).
  const playsHighlightsStream =
    isHighlightsOnlyMode && Boolean(currentItem?.highlightsOnlyStream);
  const sessionIsEmpty = !listed.length && !sessionNotes.length && !research;
  const tabs: ColumnTab[] = investigation
    ? [
        { id: "highlights", label: "Session" },
        { id: "investigation", label: "Investigation" },
      ]
    : [];
  const activeTab = tabs.some((tab) => tab.id === selectedTab)
    ? selectedTab
    : "highlights";

  return (
    <HighlightsPlaylistColumn>
      {!isFindingsShowing && (
        <button
          onClick={() => setIsFindingsShowing(true)}
          className="w-10 -left-10 flex opacity-20 hover:opacity-100 place-items-center bg-background border-background top-0 h-10  border absolute z-10"
        >
          <Info />
        </button>
      )}
      <div
        className={cn(
          !isFindingsShowing && "hidden",
          "absolute top-0 bg-background left-0 w-full p-4 bottom-0 right-0 z-10 pt-12",
        )}
      >
        <div
          className="metaDescriptionCloseButtonDiv"
          onClick={() => {
            setIsFindingsShowing(false);
          }}
        >
          <ChevronLeftCircleIcon
            className="metaDescriptionCloseButton absolute top-0 left-0 p-1 m-2"
            size={32}
          />
        </div>
        <div className="metaDescriptionBody">
          <h4>{props.meta.title}</h4>
          <h5>by {props.meta.authorName}</h5>
          {props.meta.description ? (
            // Author-written text: never parse it as HTML.
            <p className="whitespace-pre-line">{props.meta.description}</p>
          ) : (
            <>
              <p>...</p>
            </>
          )}
        </div>
      </div>
      <div className="d-flex NewRowForMetaAndTitle">
        {!isBusinessCardPlaying && (
          <h3
            id="NowPlayingTitle"
            className="mb-3.5 md:block pt-3.5 relative rounded-t-lg p-2 whitespace-nowrap cursor-pointer font-sans border-b border-background w-full flex flex-col place-items-center"
          >
            <div className="relative w-full truncate h-6">
              <div className="absolute left-0 right-0">
                <p className="truncate text-sm pl-1 w-full">
                  {currentPlaylist[currentSessionIndex]?.title}
                </p>
              </div>
            </div>
          </h3>
        )}
      </div>

      {!isBusinessCardPlaying && tabs.length > 0 && (
        <ColumnTabs
          tabs={tabs}
          active={activeTab}
          panelId="HighlightsPlaylist"
          onChange={setSelectedTab}
        />
      )}

      {!isBusinessCardPlaying && (
        <div
          // Keyed by tab so each one opens at its top.
          key={activeTab}
          id="HighlightsPlaylist"
          className="flex-1 min-h-0 w-full overflow-y-auto overflow-x-hidden overscroll-contain touch-pan-y scrollbar-stone"
          {...(tabs.length > 0
            ? { role: "tabpanel", "aria-labelledby": columnTabId(activeTab) }
            : {})}
        >
          {activeTab === "investigation" && investigation && (
            <InvestigationDetails investigation={investigation} playAt={playAt} />
          )}
          {activeTab === "highlights" && sessionIsEmpty && (
            <motion.div
              key="no_highlights"
              className="flex place-items-center justify-center h-full"
              initial={{ opacity: 0, y: -5, x: -5 }} // Slide in from the left
              animate={{ opacity: 1, y: 0, x: 0 }} // Move to the normal position
              exit={{ opacity: 0, y: 5, x: 5 }} // Exit to the right
              transition={{
                opacity: { duration: 0.1 },
                y: { duration: 0.1, ease: "easeInOut" },
                x: { duration: 0.2, ease: "easeInOut" },
              }}
            >
              <MdDragHandle
                size={18}
                className="text-black dark:text-zinc-600"
              />
            </motion.div>
          )}
          {activeTab === "highlights" &&
            listed.map(
              ({ segment: s, kind, highlightIndex }, j: number) => {
                const onHighlightsClock =
                  playsHighlightsStream && kind === "talk";
                const startTime = onHighlightsClock
                  ? s.highlightsOnlyStart
                  : s.start;
                const endTime = onHighlightsClock
                  ? s.highlightsOnlyEnd
                  : s.end;

                const playbackTimeMs = currentPlaybackTime * 1000;
                const isWithinSegment =
                  playbackTimeMs >= startTime + 50 &&
                  playbackTimeMs <= endTime - 50;
                const timeRemaining = isWithinSegment
                  ? endTime - playbackTimeMs
                  : 0;
                const sliderValue = s.length - timeRemaining; // Calculate slider position based on elapsed time

                return (
                  <HighlightBrick
                    key={s.id}
                    segment={s}
                    kind={kind}
                    highlightIndex={highlightIndex}
                    segmentIndex={j}
                    isWithinSegment={isWithinSegment}
                    sliderValue={sliderValue}
                    playsHighlightsStream={playsHighlightsStream}
                  />
                );
              },
            )}
          {activeTab === "highlights" && sessionNotes.length > 0 && (
            <section
              aria-labelledby="session-notes-heading"
              className={cn("px-2", listed.length > 0 ? "pt-4" : "pt-3")}
            >
              <h5 id="session-notes-heading" className={SECTION_HEADING}>
                Notes
              </h5>
              <SessionNotesList
                notes={sessionNotes}
                segments={currentItem?.segments ?? []}
                playsHighlightsStream={playsHighlightsStream}
                sessionIndex={currentSessionIndex}
                playAt={playAt}
              />
            </section>
          )}
          {activeTab === "highlights" && research && (
            <SessionResearch research={research} playAt={playAt} />
          )}
        </div>
      )}
      {!isBusinessCardPlaying && (
        <PlaybackSettings
          highlightsArray={currentPlaylist[currentSessionIndex]?.highlights}
          playList={currentPlaylist}
          widgetMode={props.widgetMode}
        />
      )}
    </HighlightsPlaylistColumn>
  );
}
